import {
  Avatar,
  Badge,
  Box,
  Button,
  ButtonBase,
  Divider,
  Drawer,
  List,
  ListItemButton,
  Skeleton,
  Stack,
  SvgIconProps,
  Typography,
} from '@mui/material'
import get from 'lodash/get'
import { Fragment, useMemo, useState } from 'react'
import {
  Fade,
  IconButton,
  Icons,
  Image,
  InfoModal,
  MenuListItem,
  PointDisplay,
  SkeletonText,
} from 'utoniq-ui'

import {
  FollowArtistsFragment,
  Maybe,
  MyPreSaleEventConnectionFragment,
  OrganizationArtistRankingsConnectionFragment,
  PrepaidPointBalance,
  Resource,
  ResourceDrm,
  User as UserSchema,
  UserConnectionFragment,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head, Header } from '@/components/layouts'
import { EventTicketCard } from '@/components/organisms'
import { HorizontalList } from '@/components/organisms/functionals'
import { Rank } from '@/components/organisms/ranking/Rank'
import { DRMWallpaperPlayer } from '@/components/organisms/resourcePlayer/ImagePlayer/DRMWallpaperPlayer'
import {
  SelectableVoicePlayer,
  VoicePlayer,
} from '@/components/organisms/resourcePlayer/VoicePlayer'
import { styles, styleSets } from '@/components/styles'
import { config } from '@/config'
import { User as AuthUser, useRouter, useTranslation } from '@/core'
import {
  Cell,
  CellFrame,
  Organism,
  OrganismFrame,
  Template,
} from '@/core/architecture'
import { individualHideWallet } from '@/core/individual'
import { setItemSku } from '@/core/sku'
import { filterHiddenItem } from '@/lib/array'
import { useImage } from '@/lib/image'

export * from './fragments'

export type State = {
  loading: boolean
  fetchingMyPreSaleEvents: boolean
  user: Maybe<
    UserConnectionFragment & {
      organizationFollowees: FollowArtistsFragment
    }
  >
  prepaidPointBalance: Maybe<
    Pick<PrepaidPointBalance, 'prepaidPointId' | 'balance'>
  >
  isCustom: boolean
  isArtist: boolean
  currentUser: Maybe<AuthUser>
  artistRankings: Maybe<OrganizationArtistRankingsConnectionFragment['edges']>
  events: Maybe<MyPreSaleEventConnectionFragment>
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {}

type ListProps = {
  title: string
  href: string
  hidden?: boolean
  Icon: (props: SvgIconProps) => JSX.Element
}

type ArtistCellProps = {
  loading: State['loading']
  organizationFollowees: Maybe<FollowArtistsFragment>
}

type UserCellProps = Pick<
  State,
  'user' | 'isCustom' | 'prepaidPointBalance' | 'loading' | 'currentUser'
>

type MyProfileCustomOrganismProps = Pick<
  UserConnectionFragment,
  'customProfile'
> &
  Pick<State, 'loading'>

export const MyProfileTemplate: Template<State, Action> = ({ state }) => {
  const { routerNames } = useRouter()

  return (
    <BaseLayout
      safeArea={{ pt: 0 }}
      navigation={{
        type: config.applicationType === 'standalone' ? 'artist' : 'global',
        props: {
          hasManagementTask: !!state.user?.hasManagementTask,
          unreadNotificationCount: state.user?.unreadNotificationCount,
          hasSettingTask: !!state.user?.isMembershipPersonalInformationRequired,
        },
      }}
    >
      <Head title={routerNames.MyProfile} />
      {state.isCustom && (
        <MyProfileCustomOrganism
          loading={state.loading}
          customProfile={state.user?.customProfile}
        />
      )}
      <UserInfo
        loading={state.loading}
        user={state.user}
        currentUser={state.currentUser}
        isCustom={state.isCustom}
        prepaidPointBalance={state.prepaidPointBalance}
      />
      <ButtonService />
      <EventTicketCell events={state.events} />
      <ArtistRankingCell
        rankings={state.artistRankings}
        loading={state.loading}
      />
      {config.applicationType === 'organization' && (
        <ArtistCell
          loading={state.loading}
          organizationFollowees={state.user?.organizationFollowees ?? null}
        />
      )}
      <Menu isArtist={state.isArtist} user={state.user} />
    </BaseLayout>
  )
}

const UserInfo: Cell<UserCellProps> = ({
  loading,
  user,
  currentUser,
  isCustom,
  prepaidPointBalance,
}) => {
  const { t } = useTranslation('myProfile')
  const { routerPages, router } = useRouter()
  return (
    <CellFrame sx={{ px: 0, pt: isCustom ? 0 : styles.safeAreaTop }}>
      {!isCustom && (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ py: 3, px: 3, mb: 3, height: 56 }}
        >
          <IconButton.Back size="medium" onClick={() => router?.goBack()} />
          {!!config.modules.prepaidPoint &&
            typeof prepaidPointBalance?.balance === 'number' &&
            !individualHideWallet() && (
              <PointDisplay point={prepaidPointBalance?.balance ?? ''} />
            )}
        </Stack>
      )}
      {loading ? (
        <Stack sx={{ py: 2, px: 5 }} spacing={3} alignItems="center" flex={1}>
          <Skeleton variant="circular" width={64} height={64} />
          <Stack sx={{ minWidth: 84 }}>
            <SkeletonText variant="medium" width="100%" />
            <SkeletonText variant="small" width="100%" />
          </Stack>
          <Stack sx={{ width: '100%' }} alignItems="center" spacing={1}>
            <SkeletonText variant="small" numberOfLine={2} lastWidth="50%" />
          </Stack>
        </Stack>
      ) : (
        <Stack justifyContent="center" alignItems="center" sx={{ px: 5 }}>
          <Avatar
            sx={{ width: 64, height: 64 }}
            src={currentUser?.avatarUrl ?? ''}
          />
          <Typography variant="h4" sx={{ mt: 3 }}>
            {currentUser?.displayName}
          </Typography>
          <Typography variant="overline" color="text.hint">
            {user?.formattedAccountId}
          </Typography>
          <Typography variant="caption" sx={{ mt: 3 }}>
            {user?.introduction}
          </Typography>
        </Stack>
      )}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mb: 3,
          mt: 4,
        }}
      >
        <Button
          variant="outlined"
          sx={{ py: 1 }}
          href={routerPages.MyProfileEdit}
        >
          {t('editProfile')}
        </Button>
      </Box>
    </CellFrame>
  )
}

const Menu: Cell<{ isArtist: boolean; user: State['user'] }> = ({
  isArtist,
  user,
}) => {
  const { t } = useTranslation('myProfile')
  const { routerPages } = useRouter()
  const list = useMemo<ListProps[]>(
    () => [
      {
        title: t('console'),
        hidden: !isArtist,
        href: routerPages.ConsoleDashboard,
        Icon: () => (
          <Icons.Artist sx={{ color: 'grey.400' }} fontSize="medium" />
        ),
      },
      {
        title: t('wallet'),
        hidden: !config.modules.prepaidPoint || individualHideWallet(),
        href: routerPages.Wallet,
        Icon: () => (
          <Icons.Wallet sx={{ color: 'grey.400' }} fontSize="medium" />
        ),
      },
      {
        title: t('config'),
        href: routerPages.Setting,
        Icon: () =>
          user?.isMembershipPersonalInformationRequired ? (
            <Box sx={{ position: 'relative' }}>
              <Badge
                badgeContent={''}
                color={'like' as any}
                sx={{
                  position: 'absolute',
                  color: 'white',
                  top: 8,
                  right: '-2px',
                  width: 12,
                  height: 12,
                  '& > span': {
                    fontWeight: 'bold',
                    fontSize: 9,
                    minWidth: 12,
                    height: 12,
                    bgcolor: 'like.tap',
                  },
                }}
              />
              <Icons.Setting sx={{ color: 'grey.400' }} fontSize="medium" />
            </Box>
          ) : (
            <Icons.Setting sx={{ color: 'grey.400' }} fontSize="medium" />
          ),
      },
      {
        title: t('manager'),
        href: routerPages.Management,
        Icon: () =>
          user?.hasUnappliedDeliveryItems ||
          user?.hasWaitingPaymentItems ||
          user?.isMembershipPersonalInformationRequired ? (
            <Box sx={{ position: 'relative' }}>
              <Badge
                badgeContent={''}
                color={'like' as any}
                sx={{
                  position: 'absolute',
                  color: 'white',
                  top: 8,
                  right: '-2px',
                  width: 12,
                  height: 12,
                  '& > span': {
                    fontWeight: 'bold',
                    fontSize: 9,
                    minWidth: 12,
                    height: 12,
                    bgcolor: 'like.tap',
                  },
                }}
              />
              <Icons.Management
                sx={{ color: 'grey.400', mt: 2 }}
                fontSize="medium"
              />
            </Box>
          ) : (
            <Icons.Management sx={{ color: 'grey.400' }} fontSize="medium" />
          ),
      },
    ],
    [
      isArtist,
      routerPages.ConsoleDashboard,
      routerPages.Management,
      routerPages.Setting,
      routerPages.Wallet,
      t,
      user?.hasUnappliedDeliveryItems,
      user?.isMembershipPersonalInformationRequired,
      user?.hasWaitingPaymentItems,
    ]
  )
  return (
    <CellFrame sx={{ pb: 7 }}>
      <Typography variant="h6" sx={{ color: 'grey.600', pt: 2, pb: 4, px: 5 }}>
        {t('menu')}
      </Typography>
      <List
        sx={{
          bgcolor: 'grey.50',
          color: 'text.primary',
        }}
      >
        {filterHiddenItem(list).map((item, index) => (
          <Fragment key={index}>
            <MenuListItem
              title={item.title}
              href={item.href}
              icon={
                <item.Icon
                  style={{ fontSize: 24 }}
                  sx={{ color: 'grey.600' }}
                />
              }
              sx={{
                display: 'flex',
                height: { xs: 52, md: 56 },
                borderBottom: '1px solid',
                borderColor: 'grey.100',
                py: 3,
                px: 5,
              }}
              primaryTypographyProps={{ variant: 'body1' }}
            />
          </Fragment>
        ))}
      </List>
    </CellFrame>
  )
}

const ArtistCell: Cell<ArtistCellProps> = ({
  organizationFollowees,
  loading,
}) => {
  const { t } = useTranslation('myProfile')
  const { routerPages, generatePath } = useRouter()

  if (loading) {
    return (
      <CellFrame sx={{ pt: 3, py: 4 }}>
        <Typography variant="h6" sx={{ px: 5, py: 2, color: 'grey.600' }}>
          {t('artistFollow')}
        </Typography>
        <Stack
          flexWrap="wrap"
          sx={{ overflowY: 'auto', px: 5, ...styleSets.hideScrollbar() }}
        >
          <Stack
            direction="row"
            flexWrap="nowrap"
            overflow="hidden"
            spacing={4}
            sx={{ py: 2 }}
          >
            {[1, 2, 3, 4, 5, 6].map(i => (
              <Skeleton key={i} variant="circular" width={64} height={64} />
            ))}
          </Stack>
        </Stack>
      </CellFrame>
    )
  }
  return (
    <CellFrame
      sx={{
        overflow: 'hidden',
        pt: 5,
        pb: 4,
        backgroundColor: 'background.default',
      }}
    >
      <Typography variant="h6" sx={{ px: 5, py: 2, color: 'grey.600' }}>
        {t('artistFollow')}
      </Typography>
      {!organizationFollowees?.edges?.length ? (
        <Typography
          variant="body1"
          sx={{
            py: 6,
            px: 5,
            textAlign: 'center',
          }}
        />
      ) : (
        <Stack
          flexWrap="wrap"
          sx={{ overflowY: 'auto', px: 5, ...styleSets.hideScrollbar() }}
        >
          <Stack
            direction="row"
            spacing={4}
            flexWrap="nowrap"
            overflow="hidden"
          >
            {organizationFollowees?.edges?.map(({ node }) => (
              <ButtonBase
                key={node?.artist?.id}
                href={generatePath(routerPages.ArtistTop, {
                  artistId: node?.artist?.id ?? '#',
                })}
                sx={{ borderRadius: '50%' }}
              >
                <Avatar
                  sx={{ width: 64, height: 64 }}
                  alt={node?.artist?.displayName ?? ''}
                  src={node?.artist?.avatar?.smallUri ?? ''}
                />
              </ButtonBase>
            ))}
          </Stack>
        </Stack>
      )}
    </CellFrame>
  )
}

const ButtonService: Cell = () => {
  const { t } = useTranslation('myProfile')
  const { routerPages } = useRouter()
  const listService = useMemo(
    () => [
      {
        key: 'pickup',
        hidden: !config.modules.pickup,
        title: t('pickup'),
        IconButton: (
          <IconButton.PickUp
            href={routerPages.MyOwnedTicketsEventSelection}
            sx={{ color: 'grey.500' }}
            bgcolor="grey.50"
          />
        ),
      },
      {
        key: 'item',
        hidden: !config.modules.item,
        title: t('ownedItem'),
        IconButton: (
          <IconButton.OwnedItems
            href={routerPages.MyOwnedItems}
            sx={{ color: 'grey.500' }}
            bgcolor="grey.50"
          />
        ),
      },
      {
        key: 'cart',
        hidden: !config.modules.cart,
        title: t('cart'),
        IconButton: (
          <IconButton.Cart
            href={routerPages.StoreCartList}
            sx={{ color: 'grey.500' }}
            bgcolor="grey.50"
          />
        ),
      },
      {
        key: 'trade',
        hidden: !config.modules.trade,
        title: t('applyTrade'),
        IconButton: (
          <IconButton.Market
            href={routerPages.MyTrade}
            sx={{ color: 'grey.500' }}
            bgcolor="grey.50"
          />
        ),
      },
    ],
    [
      routerPages.MyOwnedItems,
      routerPages.MyTrade,
      routerPages.MyOwnedTicketsEventSelection,
      t,
    ]
  )
  return (
    <CellFrame
      sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        height: 116,
        alignItems: 'flex-end',
      }}
    >
      {filterHiddenItem(listService).map((item, index) => (
        <Stack
          alignItems="center"
          justifyContent="center"
          key={index}
          sx={{ mx: 2 }}
        >
          {item.IconButton}
          <Typography
            variant="overline"
            color="text.secondary"
            textAlign="center"
            sx={{ mt: 2, height: 44, width: 51 }}
          >
            {item.title}
          </Typography>
        </Stack>
      ))}
    </CellFrame>
  )
}

const MyProfileCustomOrganism: Organism<MyProfileCustomOrganismProps> = ({
  customProfile,
  loading,
}) => {
  const { t } = useTranslation('myProfile')
  const { routerPages } = useRouter()
  const [open, setOpen] = useState<boolean>(false)
  const [openCustomProfileHelp, setOpenCustomProfileHelp] =
    useState<boolean>(false)
  const avatarUrl = get(
    customProfile,
    'avatarItem.avatarResources.avatarResource.mediumUri',
    null
  )

  // 背景アイテムがDRMかどうか
  const isDrm =
    get(
      customProfile,
      'wallpaperItem.wallpaperResources.wallpaperResource.drm.encryptingStatus',
      null
    ) === 'encrypted'

  const wallpaperUrl = isDrm
    ? get(
        customProfile,
        'wallpaperItem.wallpaperResources.wallpaperResource.uri',
        null
      )
    : get(
        customProfile,
        'wallpaperItem.wallpaperResources.wallpaperResource.mediumUri',
        null
      )

  const wallpaperImage = useImage(wallpaperUrl)
  const wallpaperAspectRatio =
    !!wallpaperImage?.naturalWidth && !!wallpaperImage?.naturalHeight
      ? wallpaperImage.naturalHeight / wallpaperImage.naturalWidth
      : null

  if (loading) {
    return (
      <Skeleton
        variant="rectangular"
        width="100%"
        height={400}
        sx={{ mb: 4 }}
      />
    )
  }

  return (
    <OrganismFrame
      sx={{
        display: 'flex',
        position: 'relative',
        width: '100%',
        aspectRatio: wallpaperAspectRatio
          ? `${1 / wallpaperAspectRatio}`
          : undefined,
        maxHeight: isDrm ? undefined : 800,
        mb: 5,
        pt: styles.safeAreaTop,
      }}
    >
      <Stack
        sx={{
          mr: 'auto',
          position: 'absolute',
          zIndex: 2,
          top: 12,
          right: 12,
          pt: styles.safeAreaTop,
        }}
        spacing={3}
      >
        <IconButton.Setting
          size="small"
          bgcolor="grey.50"
          color="grey.600"
          onClick={() => setOpen(true)}
        />
        <Box>
          <ProfileAudioPlayer
            audioResource={
              customProfile?.wallpaperItem?.wallpaperResources?.audioResource
            }
            voiceResource={
              customProfile?.avatarItem?.avatarResources?.voiceResource
            }
          />
        </Box>
      </Stack>

      {/* @description
        背景画像のフェードイン表示のため、画像URLと縦横比を取得してからFadeコンポーネントをレンダリングする
      */}
      {wallpaperUrl &&
        !!wallpaperImage?.naturalWidth &&
        !!wallpaperImage?.naturalHeight && (
          <Fade>
            <Fragment>
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  width: '100%',
                  height: '100%',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center',
                  backgroundSize: 'cover',
                  backgroundImage: isDrm ? 'none' : `url("${wallpaperUrl}")`,
                  filter: isDrm ? 'none' : 'blur(20px)',
                  zIndex: 0,
                }}
              >
                {isDrm && (
                  <DRMWallpaperPlayer
                    resource={
                      customProfile?.wallpaperItem?.wallpaperResources
                        ?.wallpaperResource
                    }
                  />
                )}
              </Box>
              {!isDrm && (
                <Box
                  sx={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1,
                  }}
                >
                  <Box
                    component="img"
                    src={wallpaperUrl}
                    alt=""
                    sx={{
                      maxWidth: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      backgroundColor: '#FFFFFF',
                    }}
                  />
                </Box>
              )}
            </Fragment>
          </Fade>
        )}
      <Header
        sx={{
          backgroundImage: 'unset',
          boxShadow: 'none',
          backgroundColor: 'transparent',
          pt: styles.safeAreaTop,
          zIndex: 'unset',
        }}
      />
      {avatarUrl && (
        <Fade>
          <Image
            id="avatar-img"
            src={avatarUrl}
            width={232}
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              margin: 'auto',
              zIndex: 2,
            }}
          />
        </Fade>
      )}
      <Drawer open={open} onClose={() => setOpen(false)}>
        <Stack sx={{ p: 5 }} direction="row" alignItems="center">
          <Typography
            variant="h6"
            color="grey.600"
            align="center"
            sx={{ ml: '30px', width: '100%' }}
          >
            {t('customProfileEdit')}
          </Typography>
          <IconButton.Help
            color="primary.main"
            onClick={() => setOpenCustomProfileHelp(true)}
          />
        </Stack>
        <Divider />
        <List>
          <ListItemButton
            sx={{ height: 52 }}
            href={routerPages.CustomProfileEditWallPaper}
          >
            <Typography variant="h6">{t('changeBackground')}</Typography>
          </ListItemButton>
          <ListItemButton
            sx={{ height: 52 }}
            href={routerPages.CustomProfileEditAvatar}
            divider
          >
            <Typography variant="h6">{t('changeAvatar')}</Typography>
          </ListItemButton>
          <ListItemButton
            component={Box}
            sx={{ height: 52 }}
            alignItems="center"
            onClick={() => setOpen(false)}
          >
            <Typography
              variant="h6"
              align="center"
              color="primary"
              sx={{ width: '100%' }}
            >
              {t('cancel')}
            </Typography>
          </ListItemButton>
        </List>
      </Drawer>
      <InfoModal
        open={openCustomProfileHelp}
        onClose={() => setOpenCustomProfileHelp(false)}
        type="help"
        title={t('customProfileHelpTitle')}
        content={
          <Stack spacing={4}>
            <Stack spacing={1}>
              <Typography variant="subtitle1">
                {t('changeBackground')}
              </Typography>
              <Typography variant="caption">
                {t('changeBackgroundDesc')}
              </Typography>
            </Stack>
            <Stack spacing={1}>
              <Typography variant="subtitle1">{t('changeAvatar')}</Typography>
              <Typography variant="caption">{t('changeAvatarDesc')}</Typography>
            </Stack>
          </Stack>
        }
      />
    </OrganismFrame>
  )
}

type ProfileAudioPlayerProps = {
  audioResource?: Maybe<
    Pick<Resource, 'uri' | 'hlsUri' | 'dashUri'> & {
      drm?: Maybe<Pick<ResourceDrm, 'encryptingStatus'>> | undefined
    }
  >
  voiceResource?: Maybe<
    Pick<Resource, 'uri' | 'hlsUri' | 'dashUri'> & {
      drm?: Maybe<Pick<ResourceDrm, 'encryptingStatus'>> | undefined
    }
  >
}

// 背景BGMまたはアバターボイスを再生するUI
const ProfileAudioPlayer = ({
  audioResource,
  voiceResource,
}: ProfileAudioPlayerProps) => {
  const { t } = useTranslation('myProfile')

  if (voiceResource && !audioResource)
    return <VoicePlayer color="grey.600" resource={voiceResource} />
  if (audioResource && !voiceResource)
    return <VoicePlayer color="grey.600" resource={audioResource} />
  if (audioResource && voiceResource)
    return (
      <SelectableVoicePlayer
        resources={[
          {
            label: t('wallpaper'),
            resource: audioResource,
          },
          {
            label: t('avatar'),
            resource: voiceResource,
          },
        ]}
      />
    )

  return null
}

type ArtistRankingProps = {
  rank: number
  userId: string
  user?: Maybe<Pick<UserSchema, 'avatar'>>
}
const ArtistRanking: Cell<ArtistRankingProps> = ({ rank, user, userId }) => {
  const { generatePath, routerPages } = useRouter()
  return (
    <CellFrame>
      <ButtonBase
        sx={{
          position: 'relative',
          width: 64,
          height: 64,
          borderRadius: 1,
        }}
        href={generatePath(routerPages.ArtistRanking, { artistId: userId })}
      >
        <Rank
          size="medium"
          rank={rank}
          sx={{ transform: 'translateX(-4px)' }}
        />
        <Avatar
          sx={{
            width: 28,
            height: 28,
            position: 'absolute',
            bottom: 0,
            right: 0,
            zIndex: 3,
          }}
          src={user?.avatar?.smallUri ?? ''}
        />
      </ButtonBase>
    </CellFrame>
  )
}

type EventTicketCellProps = {
  events: State['events']
}
const EventTicketCell: Cell<EventTicketCellProps> = ({ events }) => {
  const { t } = useTranslation('userProfile')
  const { routerPages } = useRouter()

  if (!config.modules.event || !config.modules.item || !events?.edges?.length)
    return null

  return (
    <HorizontalList
      title={t('eventTicket', 'イベントチケット')}
      listHref={routerPages.MyTickets}
      trans={t('toList', '一覧へ')}
      titleTypographyProps={{ variant: 'h6', color: 'grey.600' }}
      transTypographyProps={{ variant: 'subtitle1' }}
      TitleLoadingComponent={
        <Typography variant="h6" color="grey.600">
          {t('eventTicket', 'イベントチケット')}
        </Typography>
      }
      sx={{ '.items': { pt: 2, pb: 3 }, '&>.MuiBox-root': { pb: 0 } }}
      items={events?.edges ?? []}
      spacing={2}
      renderItem={({ node }, index) => (
        <Fragment key={node.id}>
          {node.myPreSaleItems?.map(({ item, skuId }, subIndex) => (
            <EventTicketCard
              key={[index, subIndex].join('-')}
              event={node}
              item={setItemSku(item as never, skuId)}
              skuId={skuId}
              width={316}
            />
          ))}
        </Fragment>
      )}
    />
  )
}

type ArtistRankingCellProps = {
  loading: boolean
  rankings: State['artistRankings']
}
const ArtistRankingCell: Cell<ArtistRankingCellProps> = ({
  loading,
  rankings,
}) => {
  const { t } = useTranslation('userProfile')
  if (!loading && !rankings?.length) {
    return null
  }
  return (
    <HorizontalList
      loading={loading}
      loadingItemLength={8}
      title={t('monthlyRanking')}
      titleTypographyProps={{ variant: 'h6', color: 'grey.600' }}
      TitleLoadingComponent={
        <Typography variant="h6" color="grey.600">
          {t('monthlyRanking')}
        </Typography>
      }
      items={rankings ?? []}
      spacing={4}
      LoadingComponent={ArtistRankingSkeleton}
      renderItem={({ node }) => (
        <ArtistRanking
          key={node?.id}
          rank={node?.latestRankingPeriod?.myRanking?.rank ?? 0}
          userId={node?.artist?.id}
          user={node?.artist}
        />
      )}
    />
  )
}

const ArtistRankingSkeleton = () => (
  <Box>
    <Stack
      justifyContent="center"
      sx={{ position: 'relative', width: 64, height: 64 }}
    >
      <Skeleton
        variant="rectangular"
        sx={{
          transform: 'translateX(8px) rotate(-45deg)',
          width: 'calc(56px/(0.707*2))',
          height: 'calc(56px/(0.707*2))',
        }}
      />
      <Skeleton
        variant="circular"
        sx={{
          width: 28,
          height: 28,
          position: 'absolute',
          bottom: 0,
          right: 0,
          zIndex: 3,
        }}
      />
    </Stack>
  </Box>
)
